import type { Graph3DRuntime } from '@packages/graph-3d';
import type { OrganizationRuntimeState } from '../types';
import type { OrganizationGraphCompileResult } from '../utils/organizationGraph';

export function buildOrganizationRuntimeView(
  graph: OrganizationGraphCompileResult,
  orgStates: Map<string, OrganizationRuntimeState>
): Graph3DRuntime {
  const nodes: NonNullable<Graph3DRuntime['nodes']> = {};
  const parentByNodeId = buildParentByNodeId(graph);

  orgStates.forEach((state, id) => {
    const node = graph.nodeMap.get(id);
    if (!node) {
      return;
    }

    nodes[id] = mapOrganizationNodeRuntime(state.status, node.kind);

    let parentId = parentByNodeId.get(id);
    while (parentId) {
      if (!nodes[parentId]) {
        const parentNode = graph.nodeMap.get(parentId);
        if (parentNode) {
          nodes[parentId] = mapOrganizationNodeRuntime('applied', parentNode.kind);
        }
      }
      parentId = parentByNodeId.get(parentId);
    }
  });

  return { nodes };
}

function buildParentByNodeId(graph: OrganizationGraphCompileResult) {
  const parentByNodeId = new Map<string, string>();
  graph.elements.forEach(element => {
    if (element.group !== 'edges') {
      return;
    }
    const source = String(element.data.source || '');
    const target = String(element.data.target || '');
    if (!source || !target) {
      return;
    }
    parentByNodeId.set(target, source);
  });
  return parentByNodeId;
}

function mapOrganizationNodeRuntime(
  status: OrganizationRuntimeState['status'],
  kind: 'company' | 'department' | 'person'
): NonNullable<Graph3DRuntime['nodes']>[string] {
  if (status === 'approved') {
    return {
      status: 'success',
      active: true,
    };
  }

  if (status === 'rejected') {
    return {
      status: 'error',
      active: true,
      color: '#ef4444',
    };
  }

  if (status === 'applied') {
    return {
      status: kind === 'person' ? 'active' : 'active',
      active: true,
    };
  }

  return {
    status: 'idle',
    active: false,
  };
}
